// src/pages/landing/sections/Section1.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "./components/LandingHeader";
import LandingAuthActions from "./components/LandingAuthActions";
import Typewriter from "./components/TypeWriter";
import LandingInfoContent from "../components/LandingInfoContent";
import LandingInfoTabs, { isLandingInfoTabKey } from "../components/LandingInfoTabs";
import Hls from "hls.js";
import clsx from "clsx";
import { useLocale } from "../../../context/LocaleContext";
import api from "../../../api/axios";

/** Трапеция экрана телефона под перспективу */
const phoneScreenClip = `polygon(
  0% 0%,
  90% 10.2%,
  100% 100%,
  0% 90%
)`;

/** Позиционирование видео в мокапе телефона */
export const videoPosition = {
  top: "111px",
  left: "70px",
  width: "310px",
  height: "600px",
  rotate: "-35.0deg",
  scale: 1,
};

const HERO_HLS_SRC = "/assets/videos/hls/hero.m3u8";
const HERO_MP4_SRC = "/assets/videos/hero.faststart.mp4";
const HERO_HLS_CONFIG = {
  autoStartLoad: true,
  startPosition: -1,
  capLevelToPlayerSize: true,
  maxBufferLength: 12,
  maxMaxBufferLength: 30,
  backBufferLength: 0,
  lowLatencyMode: false,
};
const DESIGN_W = 1920;
const DESIGN_H = 1023;
const GUEST_LANDING_TARGET = "/events/search";
const AUTH_LANDING_TARGET = "/events/search";
const INSTAGRAM_GATE_ENABLED = false;
const TAB_TRANSITION_MS = 180;
const LANDINGISH_PATHS = new Set(["/", "/landing", "/index", "/index.html"]);

function prepareInlineHeroVideo(video) {
  if (!video) return;
  video.controls = false;
  video.autoplay = true;
  video.defaultMuted = true;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = "auto";
  try {
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;
  } catch {
    // Older Safari versions expose these as read-only.
  }
  video.setAttribute("autoplay", "");
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.setAttribute("preload", "auto");
  video.setAttribute("controlsList", "nodownload nofullscreen noremoteplayback");
}

function requestHeroVideoPlayback(video) {
  if (!video) return;
  prepareInlineHeroVideo(video);
  const playPromise = video.play();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch(() => {});
  }
}

function attachSegmentedHeroVideo(video, triedMp4Ref, { onReady, onFailure } = {}) {
  if (!video) return () => {};

  let hls = null;
  let disposed = false;
  let usingMp4 = false;
  let retryTimer = null;

  const destroyHls = () => {
    if (!hls) return;
    hls.destroy();
    hls = null;
  };

  const markReady = () => {
    if (disposed) return;
    onReady?.();
    requestHeroVideoPlayback(video);
  };

  const markFailed = () => {
    if (disposed) return;
    onFailure?.();
  };

  const switchToMp4 = () => {
    if (disposed || usingMp4) return;
    destroyHls();
    if (triedMp4Ref.current) {
      markFailed();
      return;
    }
    triedMp4Ref.current = true;
    usingMp4 = true;
    prepareInlineHeroVideo(video);
    video.src = HERO_MP4_SRC;
    video.load();
    requestHeroVideoPlayback(video);
  };

  const handleVideoError = () => {
    if (usingMp4) {
      markFailed();
      return;
    }
    switchToMp4();
  };

  prepareInlineHeroVideo(video);
  video.addEventListener("loadedmetadata", markReady);
  video.addEventListener("loadeddata", markReady);
  video.addEventListener("canplay", markReady);
  video.addEventListener("playing", markReady);
  video.addEventListener("error", handleVideoError);

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = HERO_HLS_SRC;
    video.load();
    requestHeroVideoPlayback(video);
  } else if (Hls.isSupported()) {
    hls = new Hls(HERO_HLS_CONFIG);
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (disposed || !hls) return;
      hls.loadSource(HERO_HLS_SRC);
    });
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (disposed || !hls) return;
      hls.startLoad(-1);
      requestHeroVideoPlayback(video);
    });
    hls.on(Hls.Events.FRAG_BUFFERED, markReady);
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (disposed || !data?.fatal) return;
      destroyHls();
      switchToMp4();
    });
  } else {
    switchToMp4();
  }

  retryTimer = window.setTimeout(() => requestHeroVideoPlayback(video), 250);

  return () => {
    disposed = true;
    if (retryTimer) window.clearTimeout(retryTimer);
    video.removeEventListener("loadedmetadata", markReady);
    video.removeEventListener("loadeddata", markReady);
    video.removeEventListener("canplay", markReady);
    video.removeEventListener("playing", markReady);
    video.removeEventListener("error", handleVideoError);
    destroyHls();
  };
}

function normalizeInternalPath(value, fallback = "/") {
  try {
    let v = value || fallback;
    if (/%[0-9A-F]{2}/i.test(v)) v = decodeURIComponent(v);
    if (/^https?:\/\//i.test(v)) {
      try {
        const u = new URL(v);
        v = u.pathname + (u.search || "") + (u.hash || "");
      } catch {
        v = fallback;
      }
    }
    if (!v) v = fallback;
    if (!v.startsWith("/")) v = `/${v}`;
    if (v.startsWith("//")) v = v.slice(1);
    return v;
  } catch {
    return fallback;
  }
}

function isLandingEntryPath(value) {
  const normalized = normalizeInternalPath(value, "/");
  const [pathPart, searchPart = ""] = normalized.split("?");
  if (LANDINGISH_PATHS.has(pathPart || "/")) return true;
  if (pathPart !== "/assistant/core") return false;
  const params = new URLSearchParams(searchPart);
  const entry = String(params.get("assistant_entry") || "").toLowerCase();
  return entry === "landing" || entry === "landing_map";
}

function resolveGuestLandingTarget(value) {
  const normalized = normalizeInternalPath(value, "/");
  return isLandingEntryPath(normalized) ? GUEST_LANDING_TARGET : normalized;
}

function resolveAuthenticatedLandingTarget(value) {
  const normalized = normalizeInternalPath(value, "/");
  return isLandingEntryPath(normalized) ? AUTH_LANDING_TARGET : normalized;
}

function setStoredAssistantOnboardingSource(source) {
  try {
    const normalized = String(source || "").trim().toLowerCase();
    if (!normalized.startsWith("landing")) return;
    localStorage.setItem("assistant_onboarding_source", normalized);
  } catch {
    // ignore
  }
}

async function fetchLandingAuthStatus() {
  try {
    const { data } = await api.get("/accounts/auth_status/", {
      meta: {
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      },
    });
    return Boolean(data?.isAuthenticated);
  } catch {
    return false;
  }
}

/** ─── Настройки перехода/жеста ─── */
const TRANSITION_MS = 250;
const MIN_GESTURE_Y = 60;
const VERT_FACTOR = 0.6;

/***************************************************************************
 *                        Language Toggle (EN/SK)
 ***************************************************************************/
function LanguageToggle({ isAuth = false, className = "" }) {
  const { i18n } = useTranslation();
  const { setLanguage } = useLocale();

  const current = (i18n.language || "en").toLowerCase();
  const isRu = current.startsWith("ru");
  const isSk = current.startsWith("sk");
  const isCs = current.startsWith("cs");
  const isEn = !isRu && !isSk && !isCs;

  // For RU we don't show the toggle (RU is forced by region rules)
  if (isRu) return null;

  const apply = async (lang) => {
    // 1) Always switch UI immediately + persist choice for guests
    try {
      setLanguage(lang, { persist: true });
    } catch (e) {
      // fallback just in case
      try { i18n.changeLanguage(lang); } catch {}
    }

    // 2) If user is logged in, also notify backend (endpoint will be implemented next)
    if (isAuth) {
      try {
        await api.post(
          "/accounts/set-language/",
          { language: lang },
          { meta: { suppressUnauthorizedRedirect: true, authOptional: true } }
        );
      } catch (e) {
        // Ignore for now: backend will be wired next step
        // (also allows guests without noise)
      }
    }
  };

  return (
    <div
      className={clsx(
        "select-none flex items-center text-sm text-white font-bold tracking-wide leading-none",
        className
      )}
    >
      <button
        type="button"
        onClick={() => apply("en")}
        className={clsx(
          "px-0.5 py-2 transition-opacity",
          isEn ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isEn}
        aria-label="English"
      >
        EN
      </button>

      <span
        className="inline-block w-px h-4 bg-white/30 mx-2"
        aria-hidden="true"
      />

      <button
        type="button"
        onClick={() => apply("sk")}
        className={clsx(
          "px-0.5 py-2 transition-opacity",
          isSk ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isSk}
        aria-label="Slovak"
      >
        SK
      </button>
      <span className="inline-block w-px h-4 bg-white/30 mx-2" aria-hidden="true" />
      <button
        type="button"
        onClick={() => apply("cs")}
        className={clsx(
          "px-0.5 py-2 transition-opacity",
          isCs ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isCs}
        aria-label="Čeština"
      >
        CS
      </button>
    </div>
  );
}

/***************************************************************************
 *                             Mobile Hero
 ***************************************************************************/
function HeroMobile({ isInstagram, nextPath = GUEST_LANDING_TARGET, isAuth = false }) {
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [activeInfoKey, setActiveInfoKey] = useState("");
  const [displayInfoKey, setDisplayInfoKey] = useState("");
  const [isInfoContentVisible, setIsInfoContentVisible] = useState(false);

  const videoRef = useRef(null);
  const triedMp4Ref = useRef(false);
  const gateRef = useRef(null);
  const infoTransitionTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRu = i18n.language && i18n.language.startsWith("ru");
  const isSk = i18n.language && i18n.language.startsWith("sk");
  const isCs = i18n.language && i18n.language.startsWith("cs");

  const guestEntryPath = React.useMemo(() => {
    try {
      return resolveGuestLandingTarget(nextPath);
    } catch {
      return GUEST_LANDING_TARGET;
    }
  }, [nextPath]);
  // ---- ДОБАВЛЕНО: безопасная нормализация целевого пути ----
  const normalizeNext = React.useCallback((p) => {
    return normalizeInternalPath(p, guestEntryPath);
  }, [guestEntryPath]);

  const resolveEntryPath = React.useCallback(async () => {
    if (isAuth === true) {
      return {
        authenticated: true,
        targetPath: resolveAuthenticatedLandingTarget(nextPath),
      };
    }
    if (isAuth === false) {
      return {
        authenticated: false,
        targetPath: guestEntryPath,
      };
    }
    const authenticated = await fetchLandingAuthStatus();
    return {
      authenticated,
      targetPath: authenticated
        ? resolveAuthenticatedLandingTarget(nextPath)
        : guestEntryPath,
    };
  }, [guestEntryPath, isAuth, nextPath]);

  const clearInfoTransition = React.useCallback(() => {
    if (infoTransitionTimeoutRef.current) {
      window.clearTimeout(infoTransitionTimeoutRef.current);
      infoTransitionTimeoutRef.current = null;
    }
  }, []);

  const handleInfoTabChange = React.useCallback((key) => {
    if (!isLandingInfoTabKey(key)) return;

    clearInfoTransition();
    setActiveInfoKey(key);

    if (!displayInfoKey) {
      setDisplayInfoKey(key);
      setIsInfoContentVisible(false);
      requestAnimationFrame(() => setIsInfoContentVisible(true));
      return;
    }

    if (displayInfoKey === key) {
      setIsInfoContentVisible(true);
      return;
    }

    setIsInfoContentVisible(false);
    infoTransitionTimeoutRef.current = window.setTimeout(() => {
      setDisplayInfoKey(key);
      setIsInfoContentVisible(true);
      infoTransitionTimeoutRef.current = null;
    }, TAB_TRANSITION_MS);
  }, [clearInfoTransition, displayInfoKey]);

  const handleCloseInfoOverlay = React.useCallback(() => {
    clearInfoTransition();
    setActiveInfoKey("");
    setIsInfoContentVisible(false);
    infoTransitionTimeoutRef.current = window.setTimeout(() => {
      setDisplayInfoKey("");
      infoTransitionTimeoutRef.current = null;
    }, TAB_TRANSITION_MS);
  }, [clearInfoTransition]);

  useEffect(() => () => clearInfoTransition(), [clearInfoTransition]);

  // Прелоад HLS
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "fetch";
    link.href = HERO_HLS_SRC;
    link.fetchPriority = "high";
    document.head.appendChild(link);
  }, []);

  // Инициализация видео (HLS)
  useEffect(() => {
    return attachSegmentedHeroVideo(videoRef.current, triedMp4Ref, {
      onReady: () => setVideoReady(true),
      onFailure: () => setVideoFailed(true),
    });
  }, []);

  // ── СОВЕРШЕННО НОВЫЙ ПОДХОД: ПЛАВНАЯ АНИМАЦИЯ БЕЗ ВИДИМОСТИ КРАЯ ──
  useEffect(() => {
    if (!isInstagram) return;
    const root = gateRef.current || window;
    // Включаем нативный скролл ТОЛЬКО на время экрана-гейта IG
    const de = document.documentElement;
    const b  = document.body;
    const prevOverflow = {
      deO: de.style.overflow,
      bO: b.style.overflow,
      deH: de.style.height,
      bH: b.style.height,
    };
    de.style.overflow = 'auto';
    b.style.overflow  = 'auto';
    de.style.height   = 'auto';
    b.style.height    = 'auto';

    let startX = 0, startY = 0;
    let lastY = 0;
    let lastX = 0;
    let moved = false;
    let armed = false;
    let startScrollY = 0;
    let maxScrollDeltaDown = 0;

    const resetVisuals = () => {
      setTransitionProgress(0);
    };

    const startTransition = () => {
      if (armed) return;
      armed = true;
      
      // Финальная анимация перехода
      setTransitionProgress(1);
      setAnimatingOut(true);
      
      // Блокируем дальнейший скролл
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      // Отметить прохождение гейта в этой сессии
      try { sessionStorage.setItem('igGateDone', '1'); } catch {}

      // Навигируем сразу на следующий кадр, чтобы новая страница начала монтироваться немедленно
      resolveEntryPath().then(({ authenticated, targetPath: resolvedPath }) => {
        const targetPath = normalizeNext(resolvedPath);
        console.log('Navigating to:', targetPath, 'from nextPath:', nextPath);
        try {
          if (!authenticated) {
            setStoredAssistantOnboardingSource("landing");
          }
          if (!authenticated && targetPath !== GUEST_LANDING_TARGET) {
            sessionStorage.setItem("assistant_deep_link_bypass_once", "1");
          } else {
            sessionStorage.removeItem("assistant_deep_link_bypass_once");
          }
        } catch {
          // ignore session storage failures
        }

        requestAnimationFrame(() => {
          navigate(targetPath, { replace: true });
        });
      });
    };

    const onTouchStart = (e) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastY = startY;
      lastX = startX;
      moved = false;
      startScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      maxScrollDeltaDown = 0;
      resetVisuals();
    };

    const onTouchMove = (e) => {
      if (armed) {
        e.preventDefault();
        return;
      }

      const t = e.touches[0];
      const currentY = t.clientY;
      const currentX = t.clientX;
      const dy = currentY - startY; // Отрицательное значение = движение ВВЕРХ
      lastY = currentY;
      lastX = currentX;
      
      if (!moved && Math.abs(dy) > 5) {
        moved = true;
      }

      // Реальный скролл вниз страницы (для Instagram панели)
      const sy = window.pageYOffset || document.documentElement.scrollTop || 0;
      const dsy = sy - startScrollY;
      if (dsy > maxScrollDeltaDown) maxScrollDeltaDown = dsy;

      // Обрабатываем ТОЛЬКО движение ВВЕРХ (отрицательный dy)
      if (dy < 0) {
        const absDy = Math.abs(dy);
        const dx = Math.abs(t.clientX - startX);
        const mostlyVertical = dx <= VERT_FACTOR * absDy;
        
        if (mostlyVertical) {
          // Плавные визуальные изменения для жеста ВВЕРХ
          const progress = Math.min(absDy / 120, 1);
          setTransitionProgress(progress);

          // Автоматический переход при достижении порога
          if (absDy >= MIN_GESTURE_Y && maxScrollDeltaDown >= 2) {
            startTransition();
          }
        }
      } else {
        // Движение ВНИЗ - сбрасываем эффекты
        resetVisuals();
      }
    };

    const onTouchEnd = (e) => {
      if (!moved || armed) return;

      const endT = e.changedTouches && e.changedTouches[0];
      const endX = endT ? endT.clientX : lastX;
      const endY = endT ? endT.clientY : lastY;

      const absDy = Math.abs(endY - startY);
      const absDx = Math.abs(endX - startX);
      const mostlyVertical = absDx <= VERT_FACTOR * absDy;
      const swipedUp = endY < startY;

      // Оценка "прогресса" по величине жеста (для fallback-сценария)
      const progressLike = Math.min(absDy / 120, 1);

      if (swipedUp && absDy >= MIN_GESTURE_Y && mostlyVertical) {
        // Основной путь — когда IG реально даёт скролл вниз (maxScrollDeltaDown >= 2)
        // Fallback — когда жест уверенный, но scrollDelta не сработал на конкретном устройстве
        if (maxScrollDeltaDown >= 2 || progressLike >= 0.95) {
          startTransition();
          return;
        }
      }

      // Мягкий откат к исходному состоянию
      setTimeout(() => {
        if (!armed) resetVisuals();
      }, 300);
    };

    // РАЗРЕШАЕМ нативный скролл Instagram
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: true });
    root.addEventListener("touchend", onTouchEnd, { passive: true });
    
    return () => {
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      de.style.overflow = prevOverflow.deO;
      b.style.overflow  = prevOverflow.bO;
      de.style.height   = prevOverflow.deH;
      b.style.height    = prevOverflow.bH;
    };
  }, [isInstagram, navigate, nextPath, normalizeNext, guestEntryPath, resolveEntryPath]); // Добавлены зависимости

  // Префетч ассетов
  useEffect(() => {
    if (!videoReady) return;
    const u = navigator.userAgent;
    const isSafariDesktop =
      /^((?!chrome|crios|edgios|fxios|android).)*safari/i.test(u) &&
      !/(iPhone|iPad|iPod)/i.test(u);
    if (!isSafariDesktop) return;

    [
      "/assets/landing/phone.png",
      "/assets/landing/laptop2.png",
      "/assets/landing/phone3.png",
      "/assets/videos/vault.mp4",
      "/assets/videos/vault_poster.png",
    ].forEach((src) => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = src.endsWith(".mp4") ? "video" : "image";
      link.href = src;
      document.head.appendChild(link);
    });
  }, [videoReady]);

  // IG-версия с исправленной стрелкой и стилем
  if (isInstagram) {
    return (
      <div
        ref={gateRef}
        className={`relative w-full min-h-[100dvh] bg-black overflow-hidden font-inter ${animatingOut ? "vg-exiting" : ""}`}
        style={{ background: 'var(--vg-app-bg, #0b0f1a)', overscrollBehaviorY: 'none' }}
      >
        <style>{`
          @keyframes vg-bob-up {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes vg-gentle-zoom {
            0% { 
              transform: scale(1) translateY(0);
              filter: brightness(1) blur(0px);
            }
            100% { 
              transform: scale(1.15) translateY(-10%);
              filter: brightness(0.7) blur(2px);
            }
          }
          
          .vg-gentle-zoom {
            animation: vg-gentle-zoom ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
          }
          
          .vg-content-fade {
            animation: vg-content-fade ${TRANSITION_MS}ms ease-out both;
          }
          
          @keyframes vg-content-fade {
            0% { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            70% {
              opacity: 0.8;
              transform: translateY(-20px) scale(0.98);
            }
            100% { 
              opacity: 0;
              transform: translateY(-40px) scale(0.95);
            }
          }
          
          .vg-morph-overlay {
            position: absolute;
            inset: 0;
            background: var(--vg-app-bg, #0b0f1a);
            opacity: 0;
            transition: opacity ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
            z-index: 30;
          }
          .vg-morph-overlay::before {
            content: "";
            position: absolute;
            inset: -15%;
            pointer-events: none;
            background: radial-gradient(70% 70% at 50% 50%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 35%, rgba(0,0,0,0.0) 65%);
            mix-blend-mode: soft-light;
          }
          .vg-morph-overlay.active {
            opacity: 1;
          }

          @keyframes vg-gradient-move {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }

          .vg-gradient-text {
            background: linear-gradient(90deg, rgba(255,255,255,0.85) 0%, #38bdf8 25%, #3b82f6 50%, #38bdf8 75%, rgba(255,255,255,0.85) 100%);
            background-size: 200% 100%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            -webkit-text-fill-color: transparent;
            animation: vg-gradient-move 2.6s linear infinite;
            font-weight: 600;
            letter-spacing: 0.02em;
          }
          .vg-mask-fade {
            /* Smoothly fade the container edges so blur transitions into video */
            -webkit-mask-image: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%);
            mask-image: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%);
            -webkit-mask-size: 100% 100%;
            mask-size: 100% 100%;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
          }

          .vg-mask-fade-strong {
            /* Strong center, smooth fade to transparent edges */
            -webkit-mask-image: radial-gradient(closest-side, rgba(0,0,0,1) 60%, rgba(0,0,0,0.7) 80%, rgba(0,0,0,0) 100%);
            mask-image: radial-gradient(closest-side, rgba(0,0,0,1) 60%, rgba(0,0,0,0.7) 80%, rgba(0,0,0,0) 100%);
            -webkit-mask-size: 100% 100%;
            mask-size: 100% 100%;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
          }

          .vg-text-glow {
            text-shadow: 0 2px 10px rgba(0,0,0,0.45), 0 0 24px rgba(0,0,0,0.6);
          }
          /* Layered soft-edge capsule halo for smoother fade into video */
          .vg-capsule-layers { position: relative; }
          .vg-capsule-layers::before,
          .vg-capsule-layers::after {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 9999px; /* full capsule */
            pointer-events: none;
          }
          /* Inner thin ring + first soft glow (softened, lighter) */
          .vg-capsule-layers::before {
            box-shadow:
              0 0 0 1px rgba(255,255,255,0.06),   /* subtle light outline */
              0 0 10px 6px rgba(0,0,0,0.08),      /* inner soft glow (lighter) */
              0 0 22px 14px rgba(0,0,0,0.04);     /* mid glow (lighter) */
          }

          .vg-capsule-layers::after {
            box-shadow:
              0 0 0 1px rgba(0,0,0,0.04),         /* very soft outer outline */
              0 0 26px 16px rgba(0,0,0,0.05),     /* outer soft glow */
              0 0 44px 28px rgba(0,0,0,0.035);    /* far fade */
          }
          /* Nearly invisible background just to enable WebKit backdrop-blur without visible tint */
          .vg-bg-faint { background: rgba(0,0,0,0.02); }

          /* Bottom fade to hide video seam during swipe */
          .vg-bottom-fade {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 28vh; /* not too high, kicks in only near the edge */
            pointer-events: none;
            background: linear-gradient(to bottom, rgba(11,15,26,0) 0%, var(--vg-app-bg, #0b0f1a) 85%);
            opacity: 0;
            transition: opacity 180ms ease-out;
            -webkit-backdrop-filter: blur(8px);
            backdrop-filter: blur(8px);
            z-index: 12; /* above video and base dark layer, below content */
          }
        `}</style>


        {/* Основной контейнер с видео */}
        <div className="absolute inset-0 overflow-hidden">
          {!videoFailed ? (
          <video
            ref={videoRef}
            className={`vg-hero-bg-video absolute inset-0 w-full h-full object-cover pointer-events-none ${
              animatingOut ? "vg-gentle-zoom" : ""
            }`}
            style={{
              transform:
                animatingOut || transitionProgress <= 0.001
                  ? undefined
                  : `scale(${1 + transitionProgress * 0.08}) translateY(-${transitionProgress * 3}%)`,
              filter:
                animatingOut || transitionProgress <= 0.001
                  ? undefined
                  : `brightness(${1 - transitionProgress * 0.2})`,
              transition:
                animatingOut || transitionProgress <= 0.001
                  ? "none"
                  : "all 0.2s ease-out"
            }}
            poster="/assets/videos/hero-poster.png"
            fetchpriority="high"
            autoPlay
            muted
            loop
            playsInline
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            preload="auto"
            onLoadedData={() => setVideoReady(true)}
          />
          ) : (
            <img
              src="/assets/videos/hero-poster.png"
              alt="Hero poster fallback"
              className="absolute inset-0 h-full w-full object-cover pointer-events-none"
            />
          )}
          
          {/* Базовое затемнение */}
          <div 
            className="absolute inset-0 bg-black pointer-events-none transition-opacity duration-300"
            style={{ opacity: 0.4 + (transitionProgress * 0.3) }}
          />
          {/* Нижний плавный фейд для скрытия шва */}
          <div
            className="vg-bottom-fade"
            style={{
              opacity: animatingOut ? 1 : Math.max(0, Math.min(1, (transitionProgress - 0.06) / 0.22))
            }}
          />
        </div>

        {/* Морфинг оверлей для красивого перехода */}
        {(animatingOut || transitionProgress > 0.001) ? (
          <div
            className={`vg-morph-overlay ${animatingOut ? 'active' : ''}`}
            style={{
              opacity: animatingOut ? 1 : (transitionProgress * 0.7)
            }}
          />
        ) : null}

        {/* Контент */}
        <div className="relative w-full min-h-[100dvh] flex flex-col items-center justify-center px-4 py-6">
          {/* Logo + language toggle (toggle under logo) */}
          <div
            className={`absolute flex flex-col items-center z-40 transition-all duration-300 ${
              animatingOut ? "vg-content-fade" : ""
            }`}
            style={{
              top: "calc(env(safe-area-inset-top) + 24px)",
              opacity: Math.max(0, 1 - transitionProgress * 1.5),
              transform: animatingOut
                ? undefined
                : `translateY(-${transitionProgress * 15}px)`,
            }}
          >
            <div className="flex items-center gap-2">
              <img
                src="/assets/landing/castle-logo.svg"
                alt="VaultGrid"
                className="w-8 h-8"
              />
              <span className="text-white font-bold text-lg">
                vaultgrid<span className="text-xs align-super">βeta</span>
              </span>
            </div>
            <LanguageToggle isAuth={isAuth} className="mt-1" />

            <button
              type="button"
              onClick={() => navigate("/about")}
              className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-white/70 hover:text-white transition-colors"
            >
              <img
                src="/assets/landing/info-square.svg"
                alt={t('section1info')}
                className="w-4 h-4 opacity-80"
              />
              <span className="uppercase tracking-[0.16em]">
                {t('section1info')}
              </span>
            </button>
          </div>

          {/* Заголовок */}
          <div 
            className={`text-center z-40 mb-16 transition-all duration-300 ${
              animatingOut ? "vg-content-fade" : ""
            }`}
            style={{
              opacity: Math.max(0, 1 - (transitionProgress * 1.2)),
              transform: animatingOut ? undefined : `translateY(-${transitionProgress * 30}px) scale(${1 - transitionProgress * 0.05})`
            }}
          >
            <h1 className="text-white text-[40px] font-bold">
              <span className="text-sky-500"><Typewriter isMobile /></span>
              {!isRu && (
                <>
                  {" "}
                  <span className="text-white">{t('section1.hero.word_events')}</span>
                </>
              )}
            </h1>
            <p className="-mt-2 text-white/80 text-lg">{t('section1.hero.tagline')}</p>
          </div>

          {/* Хинт свайпа ВВЕРХ - ИСПРАВЛЕННАЯ ВЕРСИЯ */}
          <div
            className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center text-center z-40 transition-all duration-300"
            style={{
              bottom: "clamp(36px, calc(env(safe-area-inset-bottom) + 48px), 96px)",
              opacity: Math.max(0, 1 - (transitionProgress * 3)),
              transform: animatingOut ? `translateY(${transitionProgress * 50}px)` : undefined
            }}
          >
            {/* Анимированная стрелка над текстом — чуть толще и более прозрачная */}
            <div
              style={{
                animation: "vg-bob-up 1.2s ease-in-out infinite",
              }}
              className="mb-3 opacity-60"
            >
              <img
                src="/assets/landing/arrow.svg"
                alt={t('section1.arrows.up_alt')}
                className="w-7 h-7"
                style={{ transform: "rotate(180deg)" }}
                draggable={false}
              />
            </div>

            {/* Текст с плавной кнопочной оболочкой (button-like, плавнее, больше скругление) */}
            <div className="text-lg font-medium whitespace-nowrap tracking-wide px-6 py-3 rounded-full backdrop-blur-sm vg-bg-faint pointer-events-none vg-capsule-layers">
              <span className="vg-gradient-text">{t('section1.ig.swipe_up')}</span>
            </div>
          </div>

          {/* Индикатор прогресса */}
          {transitionProgress > 0.1 && !animatingOut && (
            <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-40">
              <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md rounded-full px-4 py-2">
                <div className="w-32 h-1 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all duration-100"
                    style={{ width: `${transitionProgress * 100}%` }}
                  />
                </div>
                <span className="text-white/70 text-sm font-medium">
                  {Math.round(transitionProgress * 100)}%
                </span>
              </div>
            </div>
          )}

          {/* Сентинел для скролла IG */}
          <div className="absolute bottom-0 left-0 w-full h-60 opacity-0" />
        </div>

        {/* Декоративный блик */}
        <div 
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-cyan-500/20 rounded-full blur-[120px] transition-opacity duration-300"
          style={{ opacity: 1 - transitionProgress }}
        />

        {/* Финальный переходный оверлей */}
        {animatingOut && (
          <div className="absolute inset-0 z-50" style={{ background: 'var(--vg-app-bg, #0b0f1a)' }} />
        )}
      </div>
    );
  }

  // Обычная мобильная/десктопная версия без IG
  return (
    <div className="relative w-full h-full overflow-hidden">
      {!videoFailed ? (
      <video
        ref={videoRef}
        className="vg-hero-bg-video absolute inset-0 w-full h-full object-cover pointer-events-none"
        poster="/assets/videos/hero-poster.png"
        fetchpriority="high"
        autoPlay
        muted
        loop
        playsInline
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        preload="auto"
        onLoadedData={() => setVideoReady(true)}
      />
      ) : (
        <img
          src="/assets/videos/hero-poster.png"
          alt="Hero poster fallback"
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
        />
      )}
      <div className="absolute inset-0 bg-black/45 pointer-events-none" />
      <div
        className="absolute left-1/2 top-[33%] h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(14,165,233,0.22) 0%, rgba(14,165,233,0.14) 35%, rgba(14,165,233,0.06) 56%, rgba(14,165,233,0.00) 78%)",
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-[52vh] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(5,10,22,0.22)_24%,rgba(5,10,22,0.58)_52%,rgba(2,6,16,0.92)_100%)] pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          opacity: activeInfoKey ? 1 : 0,
          background:
            "linear-gradient(180deg, rgba(17,36,92,0.52) 0%, rgba(5,10,22,0.78) 38%, rgba(0,0,0,0.94) 100%)",
        }}
      />

      {/* Headline */}
      <div
        className={`absolute inset-x-0 top-0 flex justify-center px-4 pt-[clamp(220px,31vh,300px)] transition-[opacity,transform] duration-300 ${
          activeInfoKey ? "pointer-events-none opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        }`}
      >
        <div className="z-10 text-center">
          <h1
            className={clsx(
              "whitespace-nowrap text-white font-bold leading-none",
              isRu
                ? "text-[clamp(29px,7.4vw,38px)]"
                : isSk || isCs
                  ? "text-[clamp(30px,7.7vw,40px)]"
                  : "text-[clamp(36px,9.4vw,48px)]"
            )}
          >
            <span>{t("section1.hero.enjoy_prefix")} </span>
            <span className="text-sky-500">
              <Typewriter isMobile wordsKey="section1.hero.enjoy_words" />
            </span>
          </h1>
        </div>
      </div>

      <div className="relative flex flex-col w-full h-full items-center justify-between px-4 py-6">
        {/* Tabs + language */}
        <div className="-mx-4 flex w-[calc(100%+2rem)] flex-col items-center px-7">
          <LandingInfoTabs activeKey={activeInfoKey} onChange={handleInfoTabChange} />
          <div
            className={`mt-4 transition-[opacity,transform] duration-300 ${
              activeInfoKey ? "pointer-events-none -translate-y-2 opacity-0" : "translate-y-0 opacity-100"
            }`}
          >
            <LanguageToggle isAuth={isAuth} />
          </div>
        </div>

        {(activeInfoKey || displayInfoKey) ? (
          <div
            className={`absolute inset-x-0 bottom-0 top-[calc(env(safe-area-inset-top)+92px)] z-30 transition-opacity duration-300 ${
              activeInfoKey ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <div
              className={`h-full w-full overflow-y-auto overscroll-contain px-6 pb-[calc(env(safe-area-inset-bottom)+104px)] pt-4 transition-[opacity,transform] duration-200 ${
                isInfoContentVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
              }`}
            >
              <LandingInfoContent activeKey={displayInfoKey} variant="landing" />
            </div>
          </div>
        ) : null}

        <div
          className={`absolute inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+42px)] z-20 flex justify-center px-7 transition-[opacity,transform] duration-300 ${
            activeInfoKey ? "pointer-events-none translate-y-8 opacity-0" : "translate-y-0 opacity-100"
          }`}
        >
          <div className="flex w-full max-w-[343px] flex-col items-center">
            <div className="mb-5 text-center">
              <h2 className="mx-auto max-w-[340px] text-[clamp(24px,7vw,31px)] font-extrabold leading-[1.08] text-white">
                <span className="block">{t("section1.auth.community_title_line1")}</span>
                <span className="block">{t("section1.auth.community_title_line2")}</span>
              </h2>
              <p className="mt-2 text-[clamp(14px,4vw,16px)] font-normal text-white/88">
                {t("section1.auth.community_subtitle")}
              </p>
            </div>
            <LandingAuthActions compact isAuth={isAuth} />
          </div>
        </div>

        <div
          className={`absolute inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+42px)] z-30 flex justify-center px-7 transition-[opacity,transform] duration-300 ${
            activeInfoKey ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-8 opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={handleCloseInfoOverlay}
            className="flex h-[55px] w-full max-w-[343px] items-center justify-center rounded-full bg-[#3f73ff] px-5 text-sm font-bold text-white shadow-[0_16px_42px_rgba(52,112,255,0.36)] transition hover:bg-[#4a7bff] md:text-base"
          >
            {t("section1.auth.back_to_main")}
          </button>
        </div>
      </div>
    </div>
  );
}

// Десктоп версия (оставляем без изменений)
export default function Section1({ isMobile, onNavigate, isAuth, isInstagram, nextPath = GUEST_LANDING_TARGET }) {
  const stageRef = useRef(null);
  const [phoneReady, setPhoneReady] = useState(false);
  const [phoneVideoFailed, setPhoneVideoFailed] = useState(false);
  const phoneVideoRef = useRef(null);
  const desktopTriedMp4Ref = useRef(false);
  const igUA = React.useMemo(() => {
    try {
      return /Instagram/i.test((typeof navigator !== "undefined" ? navigator.userAgent : "") || "");
    } catch {
      return false;
    }
  }, []);
  const isIG = INSTAGRAM_GATE_ENABLED && (Boolean(isInstagram) || igUA);
  const { t } = useTranslation();
  const resolvedNextPath = isAuth ? AUTH_LANDING_TARGET : nextPath;

  // Масштаб сцены под вьюпорт
  useEffect(() => {
    if (isMobile) return;
    const updateScale = () => {
      if (!stageRef.current) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const scale = Math.min(vw / DESIGN_W, vh / DESIGN_H);
      stageRef.current.style.transform = `translateX(-50%) scale(${scale})`;
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [isMobile]);

  // Видео для телефона в мокапе (десктоп сцена)
  useEffect(() => {
    return attachSegmentedHeroVideo(phoneVideoRef.current, desktopTriedMp4Ref, {
      onReady: () => setPhoneReady(true),
      onFailure: () => setPhoneVideoFailed(true),
    });
  }, []);

  return (
    <section
      className={`relative h-[100dvh] w-full bg-black font-inter ${
        isMobile ? "overflow-visible" : "overflow-hidden"
      }`}
    >
      {isMobile ? (
        <HeroMobile isInstagram={isIG} nextPath={resolvedNextPath} isAuth={isAuth} />
      ) : (
        <div
          ref={stageRef}
          className="absolute left-1/2 top-0 origin-top w-[1920px] h-[1023px] overflow-visible hidden sm:block"
        >
          {/* Header */}
          <Header isAuth={isAuth} onNavigate={onNavigate} ctaPath={resolvedNextPath} />

          {/* Blobs (как было) */}
          <div className="absolute w-[285px] h-[125px] left-[1351px] top-[232px] -rotate-[90deg] opacity-40 bg-white rounded-full blur-[150px] z-10" />
          <div className="absolute w-[380px] h-[130px] left-[1710px] top-[659px] rotate-[159deg] opacity-10 bg-white rounded-full blur-[100px] z-10" />
          <div className="absolute w-[597px] h-[265px] left-[1697px] -top-[220px] -rotate-[181deg] bg-black rounded-full blur-[125px] z-10" />
          <div className="absolute w-[282px] h-[141px] left-[200px] top-[758px] -rotate-[0deg] opacity-30 bg-white rounded-full blur-[120px] z-10" />
          <div className="absolute w-[450px] h-[200px] -left-[20px] top-[245px] rotate-[0deg] bg-black rounded-full blur-[80px] z-10" />
          <div className="absolute w-[537px] h-[102px] left-[393px] top-[400px] -rotate-[30deg] opacity-45 bg-sky-500 rounded-full blur-[185px] z-10" />
          <div className="absolute w-[496px] h-[102px] left-[406px] top-[570px] rotate-[51deg] opacity-45 bg-sky-500 rounded-full blur-[175px] z-10" />
          <div className="absolute w-[473px] h-[200px] left-[1208px] top-[721px] rotate-[151deg] opacity-70 bg-black rounded-full blur-[70px] z-10" />

          {/* Laptop mockup */}
          <img
            src="/assets/landing/laptop.png"
            alt={t('section1.mockups.laptop_alt')}
            className="absolute left-[1146px] -top-[130px] w-[1144px] h-[1054px] -rotate-[1deg] select-none pointer-events-none z-0"
            draggable={false}
          />

          {/* iPhone mockup */}
          <div
            className={`absolute left-[-66px] top-[325px] w-[575px] h-[571px] rotate-[20deg] select-none pointer-events-none z-0 transition-opacity ${
              phoneReady ? "opacity-100" : "opacity-0"
            }`}
            style={{ transformStyle: "preserve-3d" }}
            draggable={false}
          >
            {/* Экран телефона */}
            <div
              className="absolute"
              style={{
                top: videoPosition.top,
                left: videoPosition.left,
                width: videoPosition.width,
                height: videoPosition.height,
                overflow: "hidden",
                WebkitClipPath: phoneScreenClip,
                clipPath: phoneScreenClip,
                borderRadius: "60px",
                transform: `rotate(${videoPosition.rotate}) scale(${videoPosition.scale})`,
                transformOrigin: "top left",
              }}
            >
              {!phoneVideoFailed ? (
              <video
                ref={phoneVideoRef}
                className="vg-hero-bg-video w-full h-full object-cover"
                poster="/assets/videos/hero-poster.png"
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                preload="auto"
                onLoadedData={() => setPhoneReady(true)}
              />
              ) : (
                <img
                  src="/assets/videos/hero-poster.png"
                  alt="Hero poster fallback"
                  className="h-full w-full object-cover"
                />
              )}
            </div>

            {/* Рамка телефона */}
            <img
              src="/assets/landing/phone_frame.png"
              alt={t('section1.mockups.phone_frame_alt')}
              className="absolute top-0 left-20 w-full h-auto select-none pointer-events-none"
              draggable={false}
            />
          </div>

          {/* Headline */}
          <h1 className="absolute left-1/2 -translate-x-1/2 top-[393px] text-[7rem] leading-[143.76px] font-bold whitespace-nowrap">
            <span className="text-white">{t("section1.hero.enjoy_prefix")} </span>
            <span className="text-sky-500">
              <Typewriter isMobile={false} wordsKey="section1.hero.enjoy_words" />
            </span>
          </h1>

          {/* CTA bottom */}
          <div className="absolute inset-x-0 bottom-[42px] z-30 flex justify-center">
            <LandingAuthActions compact isAuth={isAuth} className="md:max-w-[343px]" />
          </div>
        </div>
      )}
    </section>
  );
}
